import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import T from 'i18n';
import styles from '../CaseIntelligencePage.module.scss';

interface Props {
  triggerCrawlPending: boolean;
  onRegenerate: () => void;
  onOpenCrawlSummary: () => void;
}

const CaseIntelligenceHeader: React.FC<Props> = ({
  triggerCrawlPending,
  onRegenerate,
  onOpenCrawlSummary,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isDisabled = triggerCrawlPending;
  const isMenuOpen = Boolean(menuAnchorEl);

  const buttonLabel = triggerCrawlPending
    ? T.translate('caseIntelligence.queuing')
    : T.translate('caseIntelligence.regenerate');

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleRegenerateClick = () => {
    handleCloseMenu();
    if (!isDisabled) {
      onRegenerate();
    }
  };

  const handleCrawlSummaryClick = () => {
    handleCloseMenu();
    onOpenCrawlSummary();
  };

  return (
    <div className={styles.header}>
      <div className={styles.headerRight}>
        <span>
          <IconButton
            aria-label={T.translate('caseIntelligence.caseIntelligenceActions') as string}
            aria-controls={isMenuOpen ? 'case-intelligence-actions-menu' : undefined}
            aria-expanded={isMenuOpen ? 'true' : undefined}
            aria-haspopup="menu"
            className={styles.actionsButton}
            onClick={handleOpenMenu}
            size="small"
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </span>
        <Menu
          id="case-intelligence-actions-menu"
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleCloseMenu}
          MenuListProps={{
            'aria-label': T.translate('caseIntelligence.caseIntelligenceActionsMenu') as string,
            dense: true,
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 200,
                fontSize: '1rem',
                boxShadow: '0 8px 24px rgba(58, 58, 72, 0.08)',
                border: '1px solid #e2e2ea',
                borderRadius: '10px',
                padding: '8px',
                marginTop: '2px',
              },
            },
          }}
        >
          <MenuItem onClick={handleRegenerateClick} disabled={isDisabled} sx={{ gap: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {triggerCrawlPending ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshRoundedIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={buttonLabel} primaryTypographyProps={{ fontSize: '1rem' }} />
          </MenuItem>
          <MenuItem onClick={handleCrawlSummaryClick} sx={{ gap: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SummarizeOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={T.translate('caseIntelligence.crawlSummaryMenu', {
                defaultValue: 'Crawl Summary',
              })}
              primaryTypographyProps={{ fontSize: '1rem' }}
            />
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default CaseIntelligenceHeader;
